/**
 * ``:param``-aware route matching for the guided-tour engine (issue #1956).
 *
 * A tour STEP carries a ``route`` it must be on. Most steps use an EXACT static
 * path (``/crm/farm``); a DETAIL-page step uses a pattern with a ``:param``
 * segment (``/legal/:otpId`` / ``/crm/deals/:id``) because the concrete id is
 * not known when the tour is authored.
 *
 * The engine compares ``location.pathname`` (a CONCRETE path like ``/legal/1822``)
 * against the step route to decide (a) whether it is "on route" and (b) whether
 * to navigate. A naive ``===`` breaks the pattern case: it sees ``/legal/1822 !==
 * /legal/:otpId`` and navigates to the LITERAL ``/legal/:otpId``, so React Router
 * renders the literal, ``useParams().otpId`` becomes ``":otpId"`` and the page
 * fetches a ``NaN`` record (404). This helper treats any ``:param`` segment as a
 * single-segment wildcard so the engine stays put on the concrete path.
 *
 * Static (no ``:``) routes keep EXACT-match behaviour, so every existing
 * static-route tour (admin/agent section + page tours) is unaffected -- a NO-OP.
 */

/**
 * Returns true when ``pathname`` matches ``stepRoute``, treating any ``:param``
 * segment in ``stepRoute`` as a wildcard for exactly ONE non-empty path segment.
 * An exact (``:``-free) step route still requires an exact string match.
 *
 * Examples:
 *   routeMatchesStep("/crm/farm", "/crm/farm")            -> true   (exact)
 *   routeMatchesStep("/crm/dealsX", "/crm/deals")         -> false  (exact)
 *   routeMatchesStep("/legal/1822", "/legal/:otpId")      -> true   (:param)
 *   routeMatchesStep("/crm/deals/42", "/crm/deals/:id")   -> true   (:param)
 *   routeMatchesStep("/legal", "/legal/:otpId")           -> false  (length)
 *   routeMatchesStep("/legal/1/2", "/legal/:otpId")       -> false  (length)
 *   routeMatchesStep("/legal/", "/legal/:otpId")          -> false  (empty seg)
 */
export function routeMatchesStep(
  pathname: string,
  stepRoute: string,
  options: { numericParams?: boolean } = {},
): boolean {
  // Fast path: no pattern segment -> exact string comparison (existing behaviour).
  if (!stepRoute.includes(":")) return pathname === stepRoute;

  const pathSegs = pathname.split("/");
  const routeSegs = stepRoute.split("/");
  if (pathSegs.length !== routeSegs.length) return false;

  for (let i = 0; i < routeSegs.length; i += 1) {
    const routeSeg = routeSegs[i] ?? "";
    const pathSeg = pathSegs[i] ?? "";
    if (routeSeg.startsWith(":")) {
      // A ``:param`` matches exactly one NON-EMPTY segment.
      if (!pathSeg) return false;
      // A record-id param (``numericParams``) must be an integer PK, so a
      // static sibling route such as ``/crm/deals/add-sale`` is not a record.
      if (options.numericParams && !/^\d+$/.test(pathSeg)) return false;
    } else if (routeSeg !== pathSeg) {
      return false;
    }
  }
  return true;
}

/**
 * What the engine does with the current step when ``pathname`` changes (or a
 * step becomes current). Pure so the rules are unit-testable without mounting
 * the provider:
 *
 *  - ``"stay"``     -- already on the step's route (``:param``-aware);
 *  - ``"advance"``  -- a hand-off step (``advanceOnRoute``) and the user has
 *                      opened a record matching its pattern, every ``:param``
 *                      being a numeric id (D9 WP24, #3551);
 *  - ``"cancel"``   -- off-route and the step route is a ``:param`` pattern, so
 *                      there is no concrete id to navigate to (issue #2688);
 *  - ``"navigate"`` -- off a static route: go there.
 */
export type StepRouteAction = "stay" | "advance" | "cancel" | "navigate";

export function stepRouteAction(
  pathname: string,
  step: { route: string; advanceOnRoute?: string },
): StepRouteAction {
  if (routeMatchesStep(pathname, step.route)) return "stay";
  // The hand-off advances only on a REAL record page: every ``:param`` must be
  // a numeric id. ``/crm/deals/add-sale`` (also reached by "Add Lease", as
  // ``?type=lease``) is a sibling static route that the plain ``:param``
  // wildcard would otherwise accept, carrying the tour onto a capture form.
  if (
    step.advanceOnRoute &&
    routeMatchesStep(pathname, step.advanceOnRoute, { numericParams: true })
  ) {
    return "advance";
  }
  if (step.route.includes(":")) return "cancel";
  return "navigate";
}
